/**
 * SOLID Review
 * S — Strong. One concern: the session's decisions — the account, the way into the game screen, the funnel, the
 *     ranking kept — each built from the snapshot as a `ClientSession`, asked, and answered through a
 *     `ClientDecision`. The rules are the aggregate's.
 * O — Good. A new session step is a method and a row here, and its rule on the aggregate.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the session's table and nothing else.
 * D — Strong. The session aggregate, the decision builder and the schemas.
 */
import { ClientSession } from '../../session/ClientSession';
import { ClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type { ICommandDiceRankingLoaded } from '../../ports/schemas/DiceRankingMoves';
import type {
  ICommandEmail, ICommandSignUp, ICommandVerifyCode, ICommandNickname, ICommandEmailCode, ICommandLoginCodeAnswered,
  ICommandSignedUp, ICommandSignedIn, ICommandProfileLoaded, ICommandEmailChanged, IPayloadEmail, IPayloadSignUp,
  IPayloadVerifyCode, IPayloadNickname, IPayloadEmailCode,
} from '../../ports/schemas/AuthMoves';
import type {
  ICommandSessionStarted, ICommandPlanEntry, ICommandMeAnswered, ICommandResumeValidated, ICommandPlanRestore,
  ICommandRestoreJoined, ICommandRoundRestored, ICommandPlanIntent, ICommandCreateGuest, ICommandGuestCreated,
  ICommandTableCode, ICommandTableCodeChecked, ICommandNavigate, IEntityEntryPlan, IEntitySignOut, IEntityProceed,
  IEntityIntent, IEntityRoundRead, IPayloadSeat, IPayloadGuest, IPayloadCodeLookup,
} from '../../ports/schemas/SessionMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TSessionOp =
  | 'planLoginCode' | 'loginCodeAnswered' | 'planSignUp' | 'signedUp' | 'planVerifyCode' | 'signedIn' | 'profileLoaded'
  | 'planNickname' | 'nicknameChanged' | 'planEmailChange' | 'planVerifyEmailChange' | 'emailChanged' | 'signedOut'
  | 'sessionStarted' | 'planEntry' | 'meAnswered' | 'meUnreachable' | 'resumeValidated' | 'planRestore' | 'restoreJoined'
  | 'roundRestored' | 'planIntent' | 'entryFinished'
  | 'planGuest' | 'guestCreated' | 'planTableCode' | 'tableCodeChecked' | 'navigate' | 'dismissJoinError'
  | 'diceRankingLoaded';

/**
 * THE SESSION'S DECISIONS (frontend-domain-oo P5, 2026-10-26; handbook §28.272) — the decide functions of
 * `decisions/session/auth.ts` (FE3), `entry.ts` and `funnel.ts` (FE2) and `diceRanking.ts` (P2) as a service. The
 * screens' checks and the hooks' guards are `ClientSession`'s; each method asks one, and what it changed — table and
 * session, and what the tab keeps — is the decision's.
 */
export class Session implements IServiceDecisionTable<TSessionOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TSessionOp> = {
    planLoginCode: (input, command) => this.planLoginCode(input, command),
    loginCodeAnswered: (input, command) => this.loginCodeAnswered(input, command),
    planSignUp: (input, command) => this.planSignUp(input, command),
    signedUp: (input, command) => this.signedUp(input, command),
    planVerifyCode: (input, command) => this.planVerifyCode(input, command),
    signedIn: (input, command) => this.signedIn(input, command),
    profileLoaded: (input, command) => this.profileLoaded(input, command),
    planNickname: (input, command) => this.planNickname(input, command),
    nicknameChanged: (input, command) => this.nicknameChanged(input, command),
    planEmailChange: (input, command) => this.planEmailChange(input, command),
    planVerifyEmailChange: (input, command) => this.planVerifyEmailChange(input, command),
    emailChanged: (input, command) => this.emailChanged(input, command),
    signedOut: (input) => this.signedOut(input),
    sessionStarted: (input, command) => this.sessionStarted(input, command),
    planEntry: (input, command) => this.planEntry(input, command),
    meAnswered: (input, command) => this.meAnswered(input, command),
    meUnreachable: (input) => this.meUnreachable(input),
    resumeValidated: (input, command) => this.resumeValidated(input, command),
    planRestore: (input, command) => this.planRestore(input, command),
    restoreJoined: (input, command) => this.restoreJoined(input, command),
    roundRestored: (input, command) => this.roundRestored(input, command),
    planIntent: (input, command) => this.planIntent(input, command),
    entryFinished: (input) => this.entryFinished(input),
    planGuest: (input, command) => this.planGuest(input, command),
    guestCreated: (input, command) => this.guestCreated(input, command),
    planTableCode: (input, command) => this.planTableCode(input, command),
    tableCodeChecked: (input, command) => this.tableCodeChecked(input, command),
    navigate: (input, command) => this.navigate(input, command),
    dismissJoinError: (input) => this.dismissJoinError(input),
    diceRankingLoaded: (input, command) => this.diceRankingLoaded(input, command),
  };

  // ── The account (FE3) ──────────────────────────────────────────────────────────────────────────────

  planLoginCode(input: IPropsClientInput, command: ICommandEmail): TClientDecision<IPayloadEmail> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planLoginCode(command.email));
  }

  loginCodeAnswered(input: IPropsClientInput, command: ICommandLoginCodeAnswered): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.loginCodeAnswered(command.email, command.refusal));
  }

  planSignUp(input: IPropsClientInput, command: ICommandSignUp): TClientDecision<IPayloadSignUp> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planSignUp(command));
  }

  signedUp(input: IPropsClientInput, command: ICommandSignedUp): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.signedUp(command.email);
    return ClientDecision.ofSession(input, session).acked(true);
  }

  planVerifyCode(input: IPropsClientInput, command: ICommandVerifyCode): TClientDecision<IPayloadVerifyCode> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planVerifyCode(command));
  }

  signedIn(input: IPropsClientInput, command: ICommandSignedIn): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.signedIn(command.identity);
    return ClientDecision.ofSession(input, session).acked(true);
  }

  profileLoaded(input: IPropsClientInput, command: ICommandProfileLoaded): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).acked(session.profileLoaded(command.profile));
  }

  planNickname(input: IPropsClientInput, command: ICommandNickname): TClientDecision<IPayloadNickname> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planNickname(command.displayName));
  }

  nicknameChanged(input: IPropsClientInput, command: ICommandNickname): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.nicknameChanged(command.displayName);
    return ClientDecision.ofSession(input, session).acked(true);
  }

  planEmailChange(input: IPropsClientInput, command: ICommandEmail): TClientDecision<IPayloadEmail> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planEmailChange(command.email));
  }

  planVerifyEmailChange(input: IPropsClientInput, command: ICommandEmailCode): TClientDecision<IPayloadEmailCode> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planVerifyEmailChange(command));
  }

  emailChanged(input: IPropsClientInput, command: ICommandEmailChanged): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.emailChanged(command.email);
    return ClientDecision.ofSession(input, session).acked(true);
  }

  signedOut(input: IPropsClientInput): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.signOut();
    return ClientDecision.ofSession(input, session).acked(true);
  }

  // ── Getting into the game (FE2) ────────────────────────────────────────────────────────────────────

  sessionStarted(input: IPropsClientInput, command: ICommandSessionStarted): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.sessionStarted(command);
    return ClientDecision.ofSession(input, session).acked(true);
  }

  planEntry(input: IPropsClientInput, command: ICommandPlanEntry): TClientDecision<IEntityEntryPlan> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planEntry(command.stored));
  }

  meAnswered(input: IPropsClientInput, command: ICommandMeAnswered): TClientDecision<IEntitySignOut> {
    const session = ClientSession.fromSnapshot(input);
    const dto: IEntitySignOut = { signOut: session.meAnswered(command.me) };
    return ClientDecision.ofSession(input, session).answering(dto);
  }

  meUnreachable(input: IPropsClientInput): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).acked(session.meUnreachable());
  }

  resumeValidated(input: IPropsClientInput, command: ICommandResumeValidated): TClientDecision<IEntityProceed> {
    const session = ClientSession.fromSnapshot(input);
    const dto: IEntityProceed = { proceed: session.resumeValidated(command.lookup) };
    return ClientDecision.ofSession(input, session).answering(dto);
  }

  planRestore(input: IPropsClientInput, command: ICommandPlanRestore): TClientDecision<IPayloadSeat> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planRestore(command.record));
  }

  restoreJoined(input: IPropsClientInput, command: ICommandRestoreJoined): TClientDecision<IEntityRoundRead> {
    const session = ClientSession.fromSnapshot(input);
    const dto: IEntityRoundRead = { read: session.restoreJoined(command) };
    return ClientDecision.ofSession(input, session).answering(dto);
  }

  roundRestored(input: IPropsClientInput, command: ICommandRoundRestored): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).acked(session.roundRestored(command));
  }

  planIntent(input: IPropsClientInput, command: ICommandPlanIntent): TClientDecision<IEntityIntent> {
    const session = ClientSession.fromSnapshot(input);
    const dto: IEntityIntent = { next: session.planIntent(command.coldChecked) };
    return ClientDecision.ofSession(input, session).answering(dto);
  }

  entryFinished(input: IPropsClientInput): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.entryFinished();
    return ClientDecision.ofSession(input, session).acked(true);
  }

  // ── The entry funnel (FE2) ─────────────────────────────────────────────────────────────────────────

  planGuest(input: IPropsClientInput, command: ICommandCreateGuest): TClientDecision<IPayloadGuest> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planGuest(command.displayName));
  }

  guestCreated(input: IPropsClientInput, command: ICommandGuestCreated): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.guestCreated(command.player);
    return ClientDecision.ofSession(input, session).acked(true);
  }

  planTableCode(input: IPropsClientInput, command: ICommandTableCode): TClientDecision<IPayloadCodeLookup> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.planTableCode(command.code));
  }

  tableCodeChecked(input: IPropsClientInput, command: ICommandTableCodeChecked): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).judging(session.tableCodeChecked(command.code, command.found));
  }

  navigate(input: IPropsClientInput, command: ICommandNavigate): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.navigate(command.screen);
    return ClientDecision.ofSession(input, session).acked(true);
  }

  dismissJoinError(input: IPropsClientInput): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    session.dismissJoinError();
    return ClientDecision.ofSession(input, session).acked(true);
  }

  // ── The rules the table is played by (P2) ──────────────────────────────────────────────────────────

  diceRankingLoaded(input: IPropsClientInput, command: ICommandDiceRankingLoaded): TClientDecision<IEntityTableAck> {
    const session = ClientSession.fromSnapshot(input);
    return ClientDecision.ofSession(input, session).acked(session.rankingLoaded(command.ranking));
  }
}
